const EXCLUDED_SELECTORS = [
  "[data-oh-chat-widget]",
  "script",
  "noscript",
  "iframe",
  "#drawer-portal-root",
];

function removeExcludedElements(root: ParentNode) {
  EXCLUDED_SELECTORS.forEach((selector) => {
    root.querySelectorAll(selector).forEach((element) => element.remove());
  });
}

/** Serializes visible page DOM (excluding chat widget UI) for the chatbot API. */
export function getPageBodyForChat(): string {
  if (typeof document === "undefined") return "";

  const clone = document.body.cloneNode(true) as HTMLElement;
  removeExcludedElements(clone);

  return Array.from(clone.children)
    .map((element) => element.outerHTML)
    .join("");
}

declare global {
  interface Window {
    __OH_CHAT_PAGE_CONTEXT__?: unknown;
  }
}

function isHomeLikePath(pathname?: string | null): boolean {
  if (!pathname) return false;
  const clean = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  // Home is "/<countryCode>" (e.g. "/pk") for most regions, and "/" for US.
  if (clean === "/") return true;
  return /^\/[a-z]{2}$/i.test(clean);
}

function getPublicContactSection() {
  const links = Array.from(
    document.querySelectorAll<HTMLAnchorElement>(
      'a[href^="tel:"], a[href^="mailto:"], a[href*="/contact-us"]',
    ),
  )
    .map((anchor) => ({
      text: anchor.textContent?.replace(/\s+/g, " ").trim() || "",
      url: anchor.getAttribute("href") || "",
    }))
    .filter((link) => link.url);
  const uniqueLinks = links.filter(
    (link, index) =>
      links.findIndex((candidate) => candidate.url === link.url) === index,
  );

  if (uniqueLinks.length === 0) return null;

  return {
    section_id: "site-contact",
    heading: "Office Hub contact details",
    text_summary: uniqueLinks
      .map((link) => link.text)
      .filter(Boolean)
      .join(" | "),
    links: uniqueLinks,
  };
}

/**
 * Returns the payload that should be sent as `pageBody` for the chatbot API.
 * - Home page: sends structured JSON (when available).
 * - Other pages: sends serialized DOM (legacy behavior).
 */
export function getPagePayloadForChat(pathname?: string): unknown {
  if (typeof window === "undefined") return "";

  if (isHomeLikePath(pathname) && window.__OH_CHAT_PAGE_CONTEXT__) {
    const structured = window.__OH_CHAT_PAGE_CONTEXT__;
    if (
      typeof structured !== "object" ||
      structured === null ||
      Array.isArray(structured)
    ) {
      return structured;
    }

    const page = (structured as { page?: unknown }).page;
    const contact = getPublicContactSection();
    if (
      !contact ||
      typeof page !== "object" ||
      page === null ||
      Array.isArray(page)
    ) {
      return structured;
    }

    return {
      ...structured,
      page: {
        ...page,
        [contact.section_id]: contact,
      },
    };
  }

  return getPageBodyForChat();
}
